/**
 * populationApi.js
 * 
 * API client for the global_recipients endpoints on sam-dbam.
 * Used by the Population Browser to manage the total recipient universe.
 */
import { getKernelApiBase, getOrgContext } from './cloudApi';

const base = () => getKernelApiBase(); // → REACT_APP_SAM_DBAM_URL/dbam/v1

async function request(path, opts = {}) {
    const url = `${base()}${path}`;
    // Inject org context headers so the backend can scope results to the
    // caller's organization (same pattern as cloudApi.httpRequest).
    const orgCtx = getOrgContext();
    const orgHeaders = {};
    if (orgCtx.orgId) orgHeaders['X-Org-ID'] = orgCtx.orgId;
    if (orgCtx.userId) orgHeaders['X-User-ID'] = orgCtx.userId;
    const init = { headers: { 'Content-Type': 'application/json', ...orgHeaders }, ...opts };
    if (opts.body && typeof opts.body === 'object') {
        init.body = JSON.stringify(opts.body);
    }
    const resp = await fetch(url, init);
    if (!resp.ok) {
        const text = await resp.text().catch(() => '');
        // Detect tier-gate 403 → friendly upgrade message
        if (resp.status === 403) {
            try {
                const body = JSON.parse(text);
                if (body.error === 'UPGRADE_REQUIRED') {
                    throw new Error(body.message || 'This feature requires a paid subscription.');
                }
            } catch (e) {
                if (e.message && e.message.includes('upgrade')) throw e;
            }
        }
        throw new Error(`${opts.method || 'GET'} ${path} failed (${resp.status}): ${text}`);
    }
    if (resp.status === 204) return null;
    return resp.json();
}

/**
 * Unwrap sam-dbam envelope: { data: { data: [...], pagination: {...} }, meta: {...} }
 */
function unwrap(resp) {
    if (!resp) return { items: [], pagination: null };
    const inner = resp?.data || resp;
    if (inner?.data && Array.isArray(inner.data)) {
        return { items: inner.data, pagination: inner.pagination || null };
    }
    if (Array.isArray(inner)) return { items: inner, pagination: null };
    return { items: [], pagination: null };
}

// ─── Population (global_recipients) ───

export async function listPopulation({ page = 1, perPage = 50, q = '', tag = '', channel = '', orgId = '', optedOut, staged, sortBy = '', sortOrder = '' } = {}) {
    const params = new URLSearchParams();
    if (page > 1) params.set('page', page);
    if (perPage !== 50) params.set('perPage', perPage);
    if (q) params.set('q', q);
    if (tag) params.set('tag', tag);
    if (channel) params.set('channel', channel);
    if (orgId) params.set('org_id', orgId);
    if (optedOut != null) params.set('opted_out', String(optedOut));
    if (staged != null) params.set('staged', String(staged));
    if (sortBy) params.set('sort_by', sortBy);
    if (sortOrder) params.set('sort_order', sortOrder);
    const qs = params.toString() ? `?${params}` : '';
    const resp = await request(`/recipients${qs}`);
    return unwrap(resp);
}

export async function getRecipient(id) {
    const resp = await request(`/recipients/${encodeURIComponent(id)}`);
    return resp?.data || resp;
}

export async function createRecipient(data) {
    const resp = await request('/recipients', { method: 'POST', body: data });
    return resp?.data || resp;
}

export async function updateRecipient(id, updates) {
    const resp = await request(`/recipients/${encodeURIComponent(id)}`, { method: 'PATCH', body: updates });
    return resp?.data || resp;
}

// archiveRecipient(id) was removed — it was an identical copy of deleteRecipient().
// For single-recipient archive, use archiveRecipients([id]) instead.

export async function deleteRecipient(id) {
    await request(`/recipients/${encodeURIComponent(id)}`, { method: 'DELETE' });
}

export async function bulkDeleteRecipients(ids) {
    return request('/recipients/bulk-delete', {
        method: 'POST',
        body: { recipient_ids: ids },
    });
}

// Recipient archiving is now handled via the consolidated archiveSelective() flow 
// in archiveApi.js, which stages a fragment on the server and merges it into 
// the singleton archive directory.

// ─── Tags ───

export async function listPopulationTags() {
    const resp = await request('/recipients/tags');
    const inner = resp?.data || resp;
    return Array.isArray(inner) ? inner : (inner?.data || []);
}

export async function createPopulationTag(name, color = '#3b82f6', description = '') {
    const resp = await request('/recipients/tags', {
        method: 'POST',
        body: { tag_name: name, tag_color: color, tag_description: description },
    });
    return resp?.data || resp;
}

export async function bulkAssignTag(tagId, recipientIds) {
    await request('/recipients/tags/bulk-assign', {
        method: 'POST',
        body: { tag_id: tagId, recipient_ids: recipientIds },
    });
}

export async function updatePopulationTag(tagId, updates) {
    const resp = await request(`/recipients/tags/${encodeURIComponent(tagId)}`, {
        method: 'PATCH',
        body: updates,
    });
    return resp?.data || resp;
}

export async function deletePopulationTag(tagId) {
    await request(`/recipients/tags/${encodeURIComponent(tagId)}`, { method: 'DELETE' });
}

// ─── Per-recipient tag operations ───

export async function listRecipientTags(recipientId) {
    const resp = await request(`/recipients/${encodeURIComponent(recipientId)}/tags`);
    const inner = resp?.data || resp;
    return Array.isArray(inner) ? inner : (inner?.data || []);
}

export async function assignRecipientTag(recipientId, tagId) {
    await request(`/recipients/${encodeURIComponent(recipientId)}/tags`, {
        method: 'POST',
        body: { tag_id: tagId },
    });
}

export async function removeRecipientTag(recipientId, tagId) {
    await request(`/recipients/${encodeURIComponent(recipientId)}/tags/${encodeURIComponent(tagId)}`, {
        method: 'DELETE',
    });
}

// ─── Distinct org values (for filter dropdown) ───

export async function listDistinctOrgs() {
    // Fetch a large page and extract unique org_id values
    // In a production system this would be a dedicated endpoint
    const { items } = await listPopulation({ perPage: 1000, sortBy: 'org_id', sortOrder: 'ASC' });
    const orgs = [...new Set(items.map(r => r.org_id).filter(Boolean))];
    return orgs.sort();
}

// ─── Archive Restore ───

/**
 * Restore recipients from an archive envelope via the server-side endpoint.
 * This is tier-gated — free-tier users will receive a 403.
 * @param {Object} envelope - The full ArchiveEnvelope JSON (or subset)
 * @returns {Promise<{created: number, updated: number, skipped: number, tags_restored: number, total: number}>}
 */
export async function restoreRecipientArchive(envelope) {
    const resp = await request('/recipients/archive-restore', {
        method: 'POST',
        body: envelope,
    });
    return resp;
}
